/**
 * @fileoverview Scope-to-section applicability rule for report template builders.
 * @module core-domain/reporting/reportScopeSections
 *
 * Responsibilities: - Declare which builder sections are required, optional, or not applicable
 *                     for each report data scope (singleCase / complexVisit).
 *                   - Provide the pure helpers the designers use to apply that rule on a scope switch.
 * Dependencies:     - None (builder section ids are plain strings).
 * Architecture:     - Pure domain logic shared by tenant-web and system-web builders via ui-admin.
 *                     The persisted-type counterpart (publish validation) is
 *                     COMPLEX_VISIT_DUPLICATED_SECTION_TYPES in @firesquirrel/import-core.
 * Notes:            - A parity test in local-db maps these builder ids to persisted types and asserts
 *                     both lists stay in agreement.
 */

/** @contract ReportScope — report data scope as persisted on report templates. */
export type ReportScope = 'singleCase' | 'complexVisit';

/** @contract SectionApplicability — how a scope treats one builder section. */
export type SectionApplicability = 'required' | 'optional' | 'notApplicable';

/**
 * @contract COMPLEX_VISIT_INAPPLICABLE_BUILDER_SECTIONS
 * Purpose: Builder sections whose content `complexParticipants` already renders per governed entity.
 * Rules: Disabled on a switch to complexVisit; rejected by publish validation if enabled.
 */
export const COMPLEX_VISIT_INAPPLICABLE_BUILDER_SECTIONS: readonly string[] = [
  'metadata',
  'violations',
  'photos',
  'signatures',
];

const COMPLEX_ONLY_BUILDER_SECTIONS: readonly string[] = ['complexVisitSummary', 'complexParticipants'];

/**
 * @process getSectionApplicability
 * Steps: Classify one builder section id for the given scope.
 * Inputs: Report scope and builder section id.
 * Outputs: required | optional | notApplicable.
 * Failure Handling: Unknown ids are optional; never throws.
 * Idempotency: REQUIRED — pure function.
 */
export function getSectionApplicability(scope: ReportScope, sectionId: string): SectionApplicability {
  if (scope === 'complexVisit') {
    if (COMPLEX_ONLY_BUILDER_SECTIONS.includes(sectionId)) return 'required';
    return COMPLEX_VISIT_INAPPLICABLE_BUILDER_SECTIONS.includes(sectionId) ? 'notApplicable' : 'optional';
  }
  return COMPLEX_ONLY_BUILDER_SECTIONS.includes(sectionId) ? 'notApplicable' : 'optional';
}

/**
 * @process applyScopeSectionRules
 * Steps: Force required sections on and notApplicable sections off; leave optional sections untouched.
 * Inputs: Builder section list and target scope.
 * Outputs: New section list; input objects are not mutated and unchanged entries keep identity.
 * Failure Handling: None needed.
 * Idempotency: REQUIRED — repeated application yields an equal list.
 */
export function applyScopeSectionRules<T extends { id: string; enabled: boolean }>(
  sections: readonly T[],
  scope: ReportScope,
): T[] {
  return sections.map((section) => {
    const applicability = getSectionApplicability(scope, section.id);
    const enabled = applicability === 'required' ? true : applicability === 'notApplicable' ? false : section.enabled;
    return enabled === section.enabled ? section : { ...section, enabled };
  });
}
